
import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Form, Button, Modal } from 'react-bootstrap';
import { FaPlus, FaTrash } from 'react-icons/fa';
import axios from 'axios';
import { toast ,Toaster } from 'react-hot-toast';
import { BASE_URL } from '../BaseURL';
import '../Assets/Css/galleryData.css';

export default function GalleryData() {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [videoLink, setVideoLink] = useState('');
  const [videos, setVideos] = useState([]);
  const [deleteItem, setDeleteItem] = useState(null); // object with type & uuid
  const [showModal, setShowModal] = useState(false);
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('agriToken');

  // Fetch uploaded images and videos
  const fetchImages = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/gallery/getImages`);
      if (res.data.success) setUploadedImages(res.data.data);
    } catch {
      toast.error('Failed to fetch images');
    }
  };

  const fetchVideos = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/gallery/getVideos`);
      if (res.data.success) setVideos(res.data.data);
    } catch {
      toast.error('Failed to fetch videos');
    }
  };

  useEffect(() => {
    fetchImages();
    fetchVideos();
  }, []);

const handleFileChange = (e) => {
  const files = Array.from(e.target.files);
  const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];

  // Limit total selection to 5 images
  if (files.length + selectedFiles.length > 5) {
    toast.error('⚠️ You can upload a maximum of 5 images at a time.');
    return;
  }

  // Check for invalid file types
  const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
  if (invalidFiles.length > 0) {
    toast.error('❌ Only PNG, JPG, and JPEG formats are allowed.');
    return;
  }

  const previews = files.map((file) => ({
    file,
    preview: URL.createObjectURL(file),
  }));

  setSelectedFiles((prev) => [...prev, ...previews]);
};




  const removeSelectedImage = (index) => {
    const updated = [...selectedFiles];
    updated.splice(index, 1);
    setSelectedFiles(updated);
  };

  const handleAddPhoto = async () => {
    if (selectedFiles.length === 0) return;
    const formData = new FormData();
    selectedFiles.forEach(({ file }) => formData.append('fileUrl', file));
    try {
      await axios.post(`${BASE_URL}/gallery/addImages`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success('Images uploaded successfully');
      setSelectedFiles([]);
      fileInputRef.current.value = '';
      fetchImages();
    } catch {
      toast.error('Image upload failed');
    }
  };

  const handleAddVideo = async () => {
    const match = videoLink.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([^\s&]+)/);
    if (!match || !match[1]) return toast.error('Invalid YouTube link');

    const embedUrl = `https://www.youtube.com/embed/${match[1]}`;
    try {
      await axios.post(`${BASE_URL}/gallery/addVideo`, { fileUrl: embedUrl }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Video added successfully');
      setVideoLink('');
      fetchVideos();
    } catch {
      toast.error('Video upload failed');
    }
  };

  const confirmDelete = (type, uuid) => {
    setDeleteItem({ type, uuid });
    setShowModal(true);
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    const endpoint =
      deleteItem.type === 'image' ? 'deleteImage' : 'deleteVideo';
    try {
      await axios.delete(`${BASE_URL}/gallery/${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { galleryUuid: deleteItem.uuid },
      });
      toast.success(`${deleteItem.type === 'image' ? 'Image' : 'Video'} deleted successfully`);
      setShowModal(false);
      deleteItem.type === 'image' ? fetchImages() : fetchVideos();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <>
    <Toaster position="top-center" reverseOrder={false} />
    <div className="gallery-section">
      <Container className="gallery-container py-5">
        <h2 className="gallery-title text-center mb-4">Gallery</h2>

        {/* Add Photos */}
        <Row className="mb-4 align-items-center">
          <Col lg={8}>
            <Form.Group controlId="formFile" className="mb-0">
              <Form.Label className="input-label">Add Photo:</Form.Label>
              <Form.Control
                type="file"
                multiple
                onChange={handleFileChange}
                ref={fileInputRef}
              />
            </Form.Group>
          </Col>
          <Col lg={2} className="mt-3 mt-lg-4">
            <Button className="add-btn" onClick={handleAddPhoto} disabled={selectedFiles.length === 0}>
              <FaPlus className="me-1" /> Upload
            </Button>
          </Col>
        </Row>

        {/* Preview Selected Images */}
        <Row className="mb-4">
          {selectedFiles.map(({ preview }, index) => (
            <Col key={index} xs={6} sm={4} md={3} className="mb-3 position-relative">
              <img src={preview} className="gallery-img" alt={`preview-${index}`} />
              <Button
                variant="danger"
                size="sm"
                className="position-absolute top-0 end-0 m-1"
                onClick={() => removeSelectedImage(index)}
              >
                <FaTrash />
              </Button>
            </Col>
          ))}
        </Row>

        {/* Uploaded Images */}
        <h4 className="section-subtitle">Uploaded Photos</h4>
        <Row className="mb-5">
          {uploadedImages.map((img, index) => (
            <Col key={index} xs={6} sm={4} md={3} className="mb-3 position-relative">
              <img src={`${BASE_URL}${img.fileUrl}`} alt={`Gallery ${index}`} className="gallery-img" />
              <Button
                variant="danger"
                size="sm"
                className="position-absolute top-0 end-0 m-1"
                onClick={() => confirmDelete('image', img.galleryUuid)}
              >
                <FaTrash />
              </Button>
            </Col>
          ))}
        </Row>

        {/* Add Video */}
        <Row className="mb-4 align-items-center">
          <Col lg={8}>
            <Form.Group controlId="videoLink" className="mb-0">
              <Form.Label className="input-label">Add YouTube Video:</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter YouTube link"
                value={videoLink}
                onChange={(e) => setVideoLink(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col lg={2} className="mt-3 mt-lg-4">
            <Button className="add-btn" onClick={handleAddVideo}>
              <FaPlus className="me-1" /> Add
            </Button>
          </Col>
        </Row>

        {/* Uploaded Videos */}
        <h4 className="section-subtitle">Uploaded Videos</h4>
        <Row>
          {videos.map((vid, index) => (
            <Col key={index} xs={12} sm={6} md={4} className="mb-4 position-relative">
              <div className="ratio ratio-16x9">
                <iframe src={vid.fileUrl} title={`Video-${index}`} allowFullScreen />
              </div>
              <Button
                variant="danger"
                size="sm"
                className="position-absolute top-0 end-0 m-1"
                onClick={() => confirmDelete('video', vid.galleryUuid)}
              >
                <FaTrash />
              </Button>
            </Col>
          ))}
        </Row>
      </Container>

      {/* Delete Confirmation Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this {deleteItem?.type}?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </div>
    </>
     
  );
}
